import { Calendar, Download, FileText, Mail, Package } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { apiClient } from "../../lib/api-client";
import { showToast } from "../../lib/toast";
import {
  POStatus,
  PO_STATUS_COLORS,
  PO_STATUS_LABELS,
  PurchaseOrder,
  purchaseOrderService,
} from "../../services/purchase-order.service";

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelQtyMap, setCancelQtyMap] = useState<Record<string, number>>({});
  const [showPartialCancel, setShowPartialCancel] = useState(false);

  useEffect(() => {
    if (id) {
      loadOrder();
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const data = await purchaseOrderService.getPurchaseOrder(id!);
      setOrder(data);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load purchase order",
      );
      navigate("/purchase-orders");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: POStatus) => {
    if (!order) return;

    try {
      await purchaseOrderService.updateStatus(order.id, newStatus);
      showToast.success("Purchase order status updated");
      loadOrder();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleRevise = async () => {
    if (!order) return;
    const reason = window.prompt("Reason for creating a new PO revision?");
    if (!reason?.trim()) return;
    try {
      await purchaseOrderService.revise(order.id, reason.trim());
      showToast.success("New purchase-order revision created");
      loadOrder();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to revise order");
    }
  };

  const handleCancelClick = () => {
    setCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!order) return;
    setIsCancelling(true);
    try {
      await purchaseOrderService.updateStatus(order.id, "CANCELLED");
      showToast.success("Purchase order cancelled");
      navigate("/purchase-orders");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to cancel order");
    } finally {
      setIsCancelling(false);
      setCancelModal(false);
    }
  };

  const openPartialCancel = () => {
    if (!order) return;
    const map: Record<string, number> = {};
    for (const item of order.items) {
      const pending =
        item.quantity - item.receivedQty - (item.cancelledQty || 0);
      map[item.id] = pending > 0 ? pending : 0;
    }
    setCancelQtyMap(map);
    setShowPartialCancel(true);
  };

  const handlePartialCancelSubmit = async () => {
    if (!order) return;
    const entries = Object.entries(cancelQtyMap)
      .filter(([, qty]) => qty > 0)
      .map(([itemId, cancelQty]) => ({ itemId, cancelQty }));
    if (entries.length === 0) {
      showToast.error("Enter quantity to cancel for at least one item");
      return;
    }
    setIsCancelling(true);
    try {
      await purchaseOrderService.cancelItems(order.id, entries);
      showToast.success("Items cancelled successfully");
      setShowPartialCancel(false);
      loadOrder();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to cancel items");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!order) return;

    try {
      const response = await apiClient.get(`/purchase-orders/${order.id}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO_${order.poNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success("PDF downloaded successfully");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to download PDF");
    }
  };

  const handleEmailPO = async () => {
    if (!order) return;

    const message = prompt("Add a message to the email (optional):");
    if (message === null) return; // User cancelled

    try {
      await purchaseOrderService.emailPurchaseOrder(
        order.id,
        message || undefined,
      );
      showToast.success(`Purchase order emailed to ${order.supplier?.name}`);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to send email");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          Purchase order not found
        </div>
      </div>
    );
  }

  const statusColor = PO_STATUS_COLORS[order.status];

  return (
    <div className="p-6 max-w-6xl mx-auto !text-gray-900">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/purchase-orders")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Purchase Orders
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Purchase Order #{order.poNumber}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Created on {formatDate(order.createdAt)}
            </p>
          </div>
          <div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}
            >
              {PO_STATUS_LABELS[order.status]}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Order Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">PO Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(order.poDate)}
                  </p>
                </div>
              </div>
              {order.expectedDelivery && (
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Expected Delivery</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(order.expectedDelivery)}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start">
                <Package className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Total Items</p>
                  <p className="font-medium text-gray-900">
                    {order.items?.length || 0}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-medium text-lg text-gray-900">
                    {formatCurrency(order.totalAmount)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Order Items
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      MRP
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Received
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Cancelled
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Pending
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Free
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Rate
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Trade Disc
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Scheme Disc
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Taxable
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      GST
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {order.items?.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            SKU: {item.product.sku}
                            {item.product.hsnCode && (
                              <span className="ml-2">
                                HSN: {item.product.hsnCode}
                              </span>
                            )}
                          </p>
                          {item.product.manufacturer && (
                            <p className="text-xs text-blue-500">
                              {item.product.manufacturer}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center text-sm">
                        {item.mrp ? formatCurrency(item.mrp) : "-"}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {(item.unitsPerPack || 1) > 1 ? (
                          <div>
                            <span className="font-medium text-gray-900">
                              {item.quantity}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">
                              {item.purchasePack?.packDescription ||
                                `× ${item.unitsPerPack}`}
                            </span>
                            <div className="text-xs text-blue-600 font-medium mt-0.5">
                              ={" "}
                              {item.baseQuantity ||
                                item.quantity * (item.unitsPerPack || 1)}{" "}
                              {item.product.unit}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium text-gray-900">
                              {item.quantity}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">
                              {item.product.unit}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {item.receivedQty > 0 ? (
                          <span className="text-green-600 font-medium">
                            {item.receivedQty}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {(item.cancelledQty || 0) > 0 ? (
                          <span className="text-red-600 font-medium">
                            {item.cancelledQty}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {(() => {
                          const pending =
                            item.quantity -
                            item.receivedQty -
                            (item.cancelledQty || 0);
                          return pending > 0 ? (
                            <span className="text-orange-600 font-semibold">
                              {pending}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {item.freeQuantity > 0 ? (
                          <span className="text-green-600 font-medium">
                            {item.freeQuantity}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-2 py-3 text-right text-green-600">
                        {item.discountPercent > 0 ? (
                          <>
                            -{formatCurrency(item.discountAmount)}
                            <span className="text-xs text-gray-500 ml-1">
                              ({item.discountPercent}%)
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right text-green-600">
                        {(item.schemeDiscountPercent || 0) > 0 ? (
                          <>
                            -{formatCurrency(item.schemeDiscountAmount)}
                            <span className="text-xs text-gray-500 ml-1">
                              ({item.schemeDiscountPercent}%)
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {item.taxableAmount
                          ? formatCurrency(item.taxableAmount)
                          : "-"}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {formatCurrency(item.taxAmount)}
                        <span className="text-xs text-gray-500 ml-1">
                          ({item.taxPercent}%)
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-6 border-t pt-4">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(order.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Total Discount:</span>
                  <span className="font-medium text-green-600">
                    -{formatCurrency(order.discountAmount)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">GST:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(order.taxAmount)}
                  </span>
                </div>
                <div className="flex justify-between w-72 text-lg font-bold border-t pt-2 text-gray-900">
                  <span>Net Amount:</span>
                  <span>{formatCurrency(order.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          {(order.notes || order.termsConditions) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Additional Information
              </h2>
              {order.notes && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Notes:
                  </p>
                  <p className="text-sm text-gray-600">{order.notes}</p>
                </div>
              )}
              {order.termsConditions && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Terms & Conditions:
                  </p>
                  <p className="text-sm text-gray-600">
                    {order.termsConditions}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Supplier Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Supplier Details
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">
                  {typeof order.supplier === "object"
                    ? order.supplier.name
                    : "N/A"}
                </p>
              </div>
              {typeof order.supplier === "object" && order.supplier.phone && (
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">
                    {order.supplier.phone}
                  </p>
                </div>
              )}
              {typeof order.supplier === "object" &&
                "email" in order.supplier &&
                order.supplier.email && (
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm text-gray-900">
                      {order.supplier.email}
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              {order.status === "DRAFT" && (
                <button
                  onClick={() => handleStatusUpdate("SUBMITTED")}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Submit order
                </button>
              )}

              {order.status === "SUBMITTED" && (
                <button
                  onClick={() =>
                    handleStatusUpdate(
                      order.approvalPolicy?.required
                        ? "PENDING_APPROVAL"
                        : "APPROVED",
                    )
                  }
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {order.approvalPolicy?.required
                    ? "Request approval"
                    : "Approve order"}
                </button>
              )}

              {order.status === "PENDING_APPROVAL" && (
                <button
                  onClick={() => handleStatusUpdate("APPROVED")}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Approve order
                </button>
              )}

              {order.status === "APPROVED" && (
                <button
                  onClick={() => handleStatusUpdate("SENT")}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Send to supplier
                </button>
              )}

              {order.status === "SENT" && (
                <button
                  onClick={() => handleStatusUpdate("ACKNOWLEDGED")}
                  className="w-full px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50"
                >
                  Record supplier acknowledgement
                </button>
              )}

              {["SENT", "ACKNOWLEDGED", "PARTIAL_RECEIVED"].includes(
                order.status,
              ) && (
                <button
                  onClick={() =>
                    navigate(`/goods-receipts/new?poId=${order.id}`)
                  }
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Create GRN
                </button>
              )}

              <button
                onClick={handleDownloadPDF}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>

              {["SENT", "ACKNOWLEDGED", "PARTIAL_RECEIVED"].includes(
                order.status,
              ) &&
                order.supplier &&
                typeof order.supplier === "object" &&
                "email" in order.supplier &&
                order.supplier.email && (
                  <button
                    onClick={handleEmailPO}
                    className="w-full px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center justify-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email PO to Supplier
                  </button>
                )}

              {order.status === "DRAFT" && (
                <button
                  onClick={() => navigate(`/purchase-orders/${order.id}/edit`)}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Edit Order
                </button>
              )}

              {["APPROVED", "SENT", "ACKNOWLEDGED"].includes(order.status) && (
                <button
                  onClick={handleRevise}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Create revision R{(order.revisionNumber || 1) + 1}
                </button>
              )}

              {["ACKNOWLEDGED", "PARTIAL_RECEIVED"].includes(order.status) && (
                <button
                  onClick={openPartialCancel}
                  className="w-full px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
                >
                  Cancel / short-close items
                </button>
              )}

              {order.status === "PARTIAL_RECEIVED" && (
                <button
                  onClick={() => handleStatusUpdate("SHORT_CLOSED")}
                  className="w-full px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
                >
                  Short-close all open quantities
                </button>
              )}

              {order.status !== "CANCELLED" &&
                order.status !== "RECEIVED" &&
                order.status !== "SHORT_CLOSED" &&
                order.status !== "PARTIAL_RECEIVED" && (
                  <>
                    {order.status === "SENT" && (
                      <button
                        onClick={openPartialCancel}
                        className="w-full px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
                      >
                        Cancel Items (Partial)
                      </button>
                    )}
                    <button
                      onClick={handleCancelClick}
                      className="w-full px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      Cancel Entire Order
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelModal}
        onClose={() => setCancelModal(false)}
        onConfirm={handleCancelConfirm}
        title="Cancel Purchase Order"
        message="Are you sure you want to cancel this entire purchase order? This action cannot be undone."
        confirmText="Cancel Order"
        cancelText="Keep Order"
        variant="danger"
        isLoading={isCancelling}
      />

      {/* Partial Cancel Modal */}
      {showPartialCancel && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Cancel Specific Quantities
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Enter the quantity to cancel for each item. Only pending
                quantities can be cancelled.
              </p>
              <table className="w-full text-sm mb-4">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Ordered
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Received
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Pending
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Cancel Qty
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {order.items.map((item) => {
                    const pending =
                      item.quantity -
                      item.receivedQty -
                      (item.cancelledQty || 0);
                    return (
                      <tr key={item.id}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">
                            {item.product.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.product.sku}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center font-medium">
                          {item.quantity}
                        </td>
                        <td className="px-2 py-2 text-center text-green-600 font-medium">
                          {item.receivedQty}
                        </td>
                        <td className="px-2 py-2 text-center text-orange-600 font-semibold">
                          {pending}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {pending > 0 ? (
                            <input
                              type="number"
                              min="0"
                              max={pending}
                              value={cancelQtyMap[item.id] ?? 0}
                              onChange={(e) => {
                                const val = Math.min(
                                  parseInt(e.target.value) || 0,
                                  pending,
                                );
                                setCancelQtyMap((prev) => ({
                                  ...prev,
                                  [item.id]: val,
                                }));
                              }}
                              className="w-16 px-2 py-1 border rounded text-center text-sm"
                            />
                          ) : (
                            <span className="text-gray-400 text-xs">
                              Fulfilled
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowPartialCancel(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handlePartialCancelSubmit}
                  disabled={isCancelling}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
